import { useEffect, useRef } from 'react';
import stile from './hinweis.module.css';

export const HINWEIS_DAUER_MS = 8000;

export interface HinweisDaten {
  /** Neue Meldung = neue id; startet die Anzeigedauer neu, auch bei gleichem Text. */
  id: number;
  text: string;
  aktion?: { text: string; ausfuehren: () => void };
}

/**
 * Die Live-Region steht immer im Baum und wechselt nur ihren Inhalt: Eine erst mit der
 * Meldung eingefuegte Region lesen Screenreader oft gar nicht vor.
 */
export function HinweisBereich({
  hinweis,
  onSchliessen,
  aktionGesperrt = false,
}: {
  hinweis: HinweisDaten | null;
  onSchliessen: () => void;
  /** Die Aktion ist gerade wirkungslos (etwa waehrend eines Schreibvorgangs); die Meldung bleibt. */
  aktionGesperrt?: boolean;
}) {
  const schliessen = useRef(onSchliessen);
  useEffect(() => {
    schliessen.current = onSchliessen;
  });
  const id = hinweis?.id;
  // WCAG 2.2.1 (R14-7): Solange Maus oder Fokus auf der Meldung stehen, laeuft ihre Zeit
  // nicht ab -- sonst verschwaende "Rückgängig" unter dem Zeiger oder mitten im Tabben.
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rest = useRef(HINWEIS_DAUER_MS);
  const gestartet = useRef(0);
  // Maus und Fokus getrennt (Fix-Runde 1, Minor 4): Ein Blur bei weiter darauf stehender Maus
  // darf die Zeit nicht wieder laufen lassen, und umgekehrt.
  const maus = useRef(false);
  const fokus = useRef(false);
  const pausiert = () => maus.current || fokus.current;
  const anhalten = () => {
    if (timer.current === null) return;
    clearTimeout(timer.current);
    timer.current = null;
    rest.current = Math.max(0, rest.current - (Date.now() - gestartet.current));
  };
  const starten = () => {
    if (timer.current !== null) return;
    gestartet.current = Date.now();
    timer.current = setTimeout(() => {
      timer.current = null;
      schliessen.current();
    }, rest.current);
  };
  useEffect(() => {
    // Ohne Meldung ist die Flaeche weg, und mit ihr Maus und Fokus.
    if (id === undefined) {
      maus.current = false;
      fokus.current = false;
      return;
    }
    rest.current = HINWEIS_DAUER_MS;
    if (!pausiert()) starten();
    return () => {
      if (timer.current !== null) clearTimeout(timer.current);
      timer.current = null;
    };
  }, [id]);
  const setze = (art: typeof maus, wert: boolean) => {
    art.current = wert;
    if (pausiert()) anhalten();
    else if (id !== undefined) starten();
  };

  return (
    <div className={stile.bereich} role="status">
      {hinweis && (
        <div
          className={stile.hinweis}
          onMouseEnter={() => setze(maus, true)}
          onMouseLeave={() => setze(maus, false)}
          onFocus={() => setze(fokus, true)}
          onBlur={() => setze(fokus, false)}
        >
          <span>{hinweis.text}</span>
          {hinweis.aktion && (
            <button
              type="button"
              className="eds-button eds-button--tertiary eds-button--sm"
              disabled={aktionGesperrt}
              onClick={() => {
                hinweis.aktion?.ausfuehren();
                onSchliessen();
              }}
            >
              {hinweis.aktion.text}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
